import React, { useEffect, useMemo, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Alert,
} from "reactstrap";

import BreadCrumb from "../../../Components/Common/BreadCrumb";
import TableContainer from "../../../Components/Common/TableContainer";
import Loader from "../../../Components/Common/Loader";
import ProductImagesEditView from "./ProductImagesEditView";
import { IsValidPermissions } from "Routes/AuthProtected";
import ImportExcelModal from "../../../Components/Common/ImportExcelModal";
import {
  useGetInventoryImageAttachmentByIdQuery,
  useGetInventoryImageAttachmentsQuery,
  useDeleteProductAttachmentByIdMutation
} from "slices/InventoryManagement/inventoryManagementSlice";
import { any } from "prop-types";

interface ProductImages {
  id: number;
  name: string;
  symbol?: string | null;
  description?: string | null;
  created_at: string;
  modified_at?: string | null;
  file_url?: string;
}

const ProductImageList: React.FC = () => {
  const [page, setPageState] = useState<{
    page: number;
    pageSize: number;
    search:string;
  }>({
    page: 1,
    pageSize: 10,
    search:'',
  });
  
  const [data, setData] = useState<ProductImages[]>([]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isImportExcel, setIsImportExcel] = useState<boolean>(false);
  const [currentMeasurementId, setCurrentMeasurementId] = useState<number | null>(null);
  
  const [viewImageId, setViewImageId] = useState<number | null>(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  
  // Delete modal states
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteImageId, setDeleteImageId] = useState<number | null>(null);
  const [deleteImageName, setDeleteImageName] = useState<string>("");
  const [deleteError, setDeleteError] = useState<string>("");
  const [deleteSuccess, setDeleteSuccess] = useState<boolean>(false);

  // List hook
  const {
    data: inventoryImageResponse,
    isLoading: loadingInventoryImageAttachments,
    refetch: refetchInventoryImageAttachments,
  } = useGetInventoryImageAttachmentsQuery(page);

  // Detail hook for view modal
  const { data: inventoryImageDetail, isFetching: fetchingInventoryImageDetail } =
    useGetInventoryImageAttachmentByIdQuery(viewImageId ?? undefined, {
      skip: !viewImageId,
    });

  // Delete mutation hook
  const [deleteProductAttachment, { isLoading: isDeleting, error: deleteApiError }] =
    useDeleteProductAttachmentByIdMutation();

  useEffect(() => {
    if (inventoryImageResponse?.results) {
      const sorted = [...inventoryImageResponse?.results].sort(
        (a, b) => Number(a.id) - Number(b.id)
      );
      setData(sorted);
    }
  }, [inventoryImageResponse]);

  // Open delete confirmation modal
  const openDeleteModal = (id: number, name: string) => {
    setDeleteImageId(id);
    setDeleteImageName(name);
    setDeleteError("");
    setDeleteSuccess(false);
    setShowDeleteModal(true);
  };

  // Close delete confirmation modal
  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setDeleteImageId(null);
    setDeleteImageName("");
    setDeleteError("");
    setDeleteSuccess(false);
  };

  // Handle delete confirmation
  const handleConfirmDelete = async () => {
    if (!deleteImageId) return;

    try {
      //@ts-ignore
      const response = await deleteProductAttachment(deleteImageId).unwrap();
      // Check if delete was successful (assuming API returns success message or status)

       setDeleteSuccess(true);
        // Refresh the list after successful delete
          refetchInventoryImageAttachments();
          closeDeleteModal();
    } catch (err: any) {
      console.error("Delete failed:", err);
      setDeleteError(
        err?.data?.message || 
        err?.error || 
        "Failed to delete product image. Please try again."
      );
    }
  };

  // handlers for edit/create modal
  const openCreateModal = () => {
    setCurrentMeasurementId(null);
    setIsEditMode(false);
    setShowEditModal(true);
  };

  const openEditModal = (id: number) => {
    setCurrentMeasurementId(id);
    setIsEditMode(true);
    setShowEditModal(true);
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setCurrentMeasurementId(null);
    setIsEditMode(false);
  };

  const handleSaved = () => {
    closeEditModal();
    refetchInventoryImageAttachments();
  };

  // handlers for view modal
  const openViewModal = (id: number) => {
    setViewImageId(id);
    setIsViewModalOpen(true);
  };

  const closeViewModal = () => {
    setIsViewModalOpen(false);
    setViewImageId(null);
  };

  const baseColumns = useMemo(
    () => [
      {
        Header: "Image",
        accessor: "file_url",
        Cell: (row: any) => (
          row.row.original.file_url ? (
            <img
              src={row.row.original.file_url}
              alt={row.row.original.name}
              className="img-fluid mb-3"
              style={{
                maxHeight: "150px",
                objectFit: "contain",
              }}
            />
          ) : (
            <div
              className="bg-light rounded d-flex align-items-center justify-content-center mb-3"
              style={{ height: "150px" }}
            >
              <i className="ri-image-line fs-1 text-muted"></i>
            </div>
          )
        ),
      },
      {
        Header: "Name",
        accessor: "name",
        Cell: (row: any) => (
          <span className="text-body">{row.row.original.name}</span>
        ),
      },
      {
        Header: "Description",
        accessor: "description",
        Cell: (cell: any) =>
          cell.row.original.description ? (
            <span>{cell.row.original.description}</span>
          ) : (
            <span className="text-muted">—</span>
          ),
      },
      {
        Header: "Created",
        accessor: "created_at",
        Cell: (cell: any) =>
          new Date(cell.row.original.created_at).toLocaleDateString(),
      },
      {
        Header: "Action",
        Cell: (cellProps: any) => {
          const m: ProductImages = cellProps.row.original;
          return (
            <UncontrolledDropdown>
              <DropdownToggle
                className="btn btn-soft-secondary btn-sm"
                tag="button"
              >
                <i className="ri-more-fill" />
              </DropdownToggle>
              <DropdownMenu className="dropdown-menu-end">
                {(IsValidPermissions(["VIEW_ONLY_INVENTORY_MEASUREMENT"]) || IsValidPermissions(["VIEW_ALL_INVENTORY_MEASUREMENT"])) &&
                  <DropdownItem
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      openViewModal(m.id);
                    }}
                  >
                    <i className="ri-eye-fill align-bottom me-2 text-muted" />{" "}
                    View
                  </DropdownItem>
                }
                <DropdownItem
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    openEditModal(m.id);
                  }}
                >
                  <i className="ri-pencil-fill align-bottom me-2 text-muted" />{" "}
                  Edit Description
                </DropdownItem>

                {/* {IsValidPermissions(["DELETE_INVENTORY_MEASUREMENT"]) && (                )} */}

                  <>
                    <DropdownItem divider />
                    <DropdownItem
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        openDeleteModal(m.id, m.name);
                      }}
                      className="text-danger"
                    >
                      <i className="ri-delete-bin-fill align-bottom me-2" />{" "}
                      Delete
                    </DropdownItem>
                  </>
              </DropdownMenu>
            </UncontrolledDropdown>
          );
        },
      },
    ],
    []
  );

  const columns = useMemo(
    () => baseColumns.map((c) => ({ ...c, disableSortBy: true })),
    [baseColumns]
  );

  document.title = "Product Images | Resource Inventory";

  if (loadingInventoryImageAttachments) {
    return (
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="Product Images" pageTitle="Resource Inventory" />
          <div className="text-center py-5">
            <Loader />
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title="Product Images" pageTitle="Resource Inventory" />

        <ImportExcelModal
          show={isImportExcel}
          onCloseClick={() => setIsImportExcel(false)}
          import_type={"product_image_import"}
          refetch={refetchInventoryImageAttachments}
        />

        <Row>
          <Col xl={12}>
            <Card>
              <div className="card-header border-0 d-flex align-items-center justify-content-between flex-wrap">
        <h5 className="mb-0">Product Images</h5>

        <div className="d-flex align-items-center gap-2 mt-2 mt-lg-0">

          {/* Search box */}
              <div className="ms-3 search-box-wrapper">
                <div className="input-group input-group-md">
                  <span className="input-group-text bg-white border-end-0">
                    <i className="ri-search-line"></i>
                  </span>
                  <input
                    type="text"
                    className="form-control border-start-0"
                    placeholder="Search images..."
                    value={page?.search}
                    onChange={(e) => {
                      setPageState((prev) => ({
                        ...prev,
                        search: e.target.value,
                        page: 1,
                      }));
                    }}
                  />
                  {page?.search && (
                    <button
                      className="btn btn-light border-start-0"
                      type="button"
                      onClick={() =>
                        setPageState((prev) => ({
                          ...prev,
                          search: "",
                          page: 1,
                        }))
                      }
                      aria-label="Clear search"
                    >
                      <i className="ri-close-line"></i>
                    </button>
                  )}
                </div>
              </div>
          {/* Buttons group */}
          {IsValidPermissions(["ADD_INVENTORY_MEASUREMENT"]) && (
            <>
              <div className="d-flex gap-2 flex-wrap">
                <Button color="primary" className="me-2" onClick={openCreateModal}>
                  Add Images
                </Button>

                <Button color="secondary" onClick={() => setIsImportExcel(true)}>
                  Import Product Image
                </Button>
              </div>

              
            </>
          )}
        </div>
      </div>


              <div className="card-body pt-0">
                {data && data?.length > 0 ? (
                  <TableContainer
                    columns={columns}
                    data={data}
                    isGlobalFilter={false}
                    isPagination={false}
                    isServerPagination={true}
                    customPageSize={page.pageSize}
                    divClass="table-responsive mb-1"
                    tableClass="mb-0 align-middle table-borderless"
                    theadClass="table-light text-muted"
                    SearchPlaceholder="Search By Image Name..."
                    serverPaginationProps={{
                      count: inventoryImageResponse?.count || 0,
                      next: inventoryImageResponse?.next || null,
                      page_size: inventoryImageResponse?.page_size || 10,
                      previous: inventoryImageResponse?.previous || null,
                      current_page: inventoryImageResponse?.current_page || 1,
                      total_page: inventoryImageResponse?.total_page || 0,
                    }}
                    setPageState={setPageState}
                  />
                ) : (
                  <div className="py-4 text-center">
                    <div>
                      <i className="ri-search-line display-5 text-success" />
                    </div>
                    <div className="mt-4">
                      <h5>Sorry! No Measurements Found</h5>
                      <p className="text-muted">
                        Click "Add Measurement" to create one.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </Card>
          </Col>
        </Row>

        {/* Edit/Create Modal */}
        <Modal isOpen={showEditModal} toggle={closeEditModal} size="lg">
          <ModalHeader toggle={closeEditModal}>
            {isEditMode ? "Edit Product Images" : "Create Product Images"}
          </ModalHeader>
          <ModalBody>
            <ProductImagesEditView
              attachmentId={isEditMode ? currentMeasurementId ?? undefined : undefined}
              compact={true}
              onSaved={handleSaved}
            />
          </ModalBody>
        </Modal>

        {/* View Modal */}
        <Modal isOpen={isViewModalOpen} toggle={closeViewModal} size="lg">
          <ModalHeader toggle={closeViewModal}>Product Images Details</ModalHeader>
          <ModalBody>
            {fetchingInventoryImageDetail ? (
              <div className="text-center py-4">
                <Loader />
              </div>
            ) : inventoryImageDetail ? (
              <div className="table-card">
                <table className="table mb-0">
                  <tbody>
                    <tr>
                      <td className="fw-medium">Name</td>
                      <td>{inventoryImageDetail.name}</td>
                    </tr>
                    <tr>
                      <td className="fw-medium">Description</td>
                      <td>{inventoryImageDetail.description ?? "—"}</td>
                    </tr>
                    <tr>
                      <td className="fw-medium">Created</td>
                      <td>
                        {new Date(
                          inventoryImageDetail.created_at
                        ).toLocaleString()}
                      </td>
                    </tr>
                    <tr>
                      <td className="fw-medium">Modified</td>
                      <td>
                        {inventoryImageDetail.modified_at
                          ? new Date(
                            inventoryImageDetail.modified_at
                          ).toLocaleString()
                          : "-"}
                      </td>
                    </tr>
                    <tr>
                      <td className="fw-medium">Product Image</td>
                      <td>
                        <img
                          src={inventoryImageDetail.file_url}
                          alt={inventoryImageDetail.name}
                          className="img-fluid mb-3"
                          style={{
                            maxHeight: "150px",
                            objectFit: "contain",
                          }}
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            ) : (
              <p>No details available.</p>
            )}
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={closeViewModal}>
              Close
            </Button>
          </ModalFooter>
        </Modal>

        {/* Delete Confirmation Modal */}
        <Modal isOpen={showDeleteModal} toggle={closeDeleteModal}>
          <ModalHeader toggle={closeDeleteModal}>
            Confirm Delete
          </ModalHeader>
          <ModalBody>
            {deleteSuccess ? (
              <Alert color="success" className="text-center">
                <i className="ri-checkbox-circle-line fs-2"></i>
                <h5 className="mt-2">Product Image Deleted Successfully!</h5>
                <p className="mb-0">The product image has been removed.</p>
              </Alert>
            ) : (
              <>
                {deleteError && (
                  <Alert color="danger" className="mb-3">
                    <i className="ri-error-warning-line me-2"></i>
                    {deleteError}
                  </Alert>
                )}
                
                <div className="text-center mb-4">
                  <div className="avatar-lg mx-auto mb-4">
                    <div className="avatar-title bg-light text-danger rounded-circle fs-1">
                      <i className="ri-delete-bin-6-line"></i>
                    </div>
                  </div>
                  <h5>Are you sure you want to delete this product image?</h5>
                  <p className="text-muted">
                    You are about to delete <strong>"{deleteImageName}"</strong>.
                    This action cannot be undone.
                  </p>
                </div>
              </>
            )}
          </ModalBody>
          <ModalFooter>
            {!deleteSuccess && (
              <>
                <Button 
                  color="light" 
                  onClick={closeDeleteModal}
                  disabled={isDeleting}
                >
                  Cancel
                </Button>
                <Button 
                  color="danger" 
                  onClick={handleConfirmDelete}
                  disabled={isDeleting}
                >
                  {isDeleting ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Deleting...
                    </>
                  ) : (
                    "Yes, Delete It"
                  )}
                </Button>
              </>
            )}
          </ModalFooter>
        </Modal>
      </Container>
    </div>
  );
};

export default ProductImageList;
